import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const BottomTabNavigation = ({ className = "" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [alertCount, setAlertCount] = useState(3);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'LayoutDashboard',
      tooltip: 'Real-time system overview and monitoring hub'
    },
    {
      label: 'Logs',
      path: '/access-logs',
      icon: 'FileText',
      tooltip: 'Comprehensive audit trail and security investigation'
    },
    {
      label: 'Alerts',
      path: '/alerts-notifications',
      icon: 'Bell',
      tooltip: 'Active security events and notifications',
      badge: alertCount > 0 ? alertCount : null
    },
    {
      label: 'Analytics',
      path: '/analytics',
      icon: 'BarChart3',
      tooltip: 'Security patterns analysis and reporting'
    },
    {
      label: 'Settings',
      path: '/settings',
      icon: 'Settings',
      tooltip: 'System configuration and user management'
    }
  ];

  useEffect(() => {
    // Simulate real-time alert updates
    const alertInterval = setInterval(() => {
      setAlertCount(prev => {
        const change = Math.floor(Math.random() * 3) - 1; // -1, 0, or 1
        return Math.max(0, prev + change);
      });
    }, 15000);

    return () => clearInterval(alertInterval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleNavigation = (path, label) => {
    navigate(path);
    
    // Clear alert badge when navigating to alerts
    if (path === '/alerts-notifications' && alertCount > 0) {
      setTimeout(() => setAlertCount(0), 1000);
    }

    // Haptic feedback for mobile devices
    if ('vibrate' in navigator) {
      navigator.vibrate(50);
    }
  };

  const isActive = (path) => {
    if (path === '/dashboard' && location?.pathname === '/') {
      return true;
    }
    return location?.pathname === path;
  };

  return (
    <>
      {/* Desktop Navigation - Horizontal Tab Bar */}
      <nav className={`hidden md:block fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border security-shadow-card ${className}`}>
        <div className="flex items-center justify-center h-16 px-4">
          <div className="flex items-center space-x-1 bg-muted/50 rounded-lg p-1">
            {navigationItems?.map((item) => {
              const active = isActive(item?.path);
              return (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path, item?.label)}
                  className={`
                    relative flex items-center space-x-2 px-4 py-2 rounded-md security-transition
                    min-w-[120px] justify-center group
                    ${active 
                      ? 'bg-primary text-primary-foreground shadow-sm' 
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }
                  `}
                  title={item?.tooltip}
                >
                  <div className="relative">
                    <Icon 
                      name={item?.icon} 
                      size={18} 
                      className={active ? 'text-primary-foreground' : 'text-current'}
                    />
                    {item?.badge && (
                      <span className="absolute -top-2 -right-2 bg-error text-error-foreground text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-medium security-pulse">
                        {item?.badge > 99 ? '99+' : item?.badge}
                      </span>
                    )}
                  </div>
                  <span className={`text-sm font-medium ${active ? 'text-primary-foreground' : 'text-current'}`}>
                    {item?.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>
      {/* Mobile Navigation - Bottom Tab Bar */}
      <nav className={`
        md:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border security-shadow-card
        transform transition-transform duration-300 ease-out
        ${isVisible ? 'translate-y-0' : 'translate-y-full'}
        ${className}
      `}>
        <div className="flex items-center justify-around h-16 px-2">
          {navigationItems?.map((item) => {
            const active = isActive(item?.path);
            return (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path, item?.label)}
                className={`
                  relative flex flex-col items-center justify-center space-y-1 p-2 rounded-lg security-transition
                  min-w-[60px] min-h-[48px] group
                  ${active 
                    ? 'text-primary' :'text-muted-foreground active:text-foreground'
                  }
                `}
                title={item?.tooltip}
              >
                <div className="relative">
                  <Icon 
                    name={item?.icon} 
                    size={20} 
                    className={`${active ? 'text-primary' : 'text-current group-active:scale-95'} security-transition`}
                  />
                  {item?.badge && (
                    <span className="absolute -top-1 -right-1 bg-error text-error-foreground text-xs rounded-full min-w-[16px] h-[16px] flex items-center justify-center font-medium security-pulse">
                      {item?.badge > 9 ? '9+' : item?.badge}
                    </span>
                  )}
                </div>
                <span className={`text-xs font-medium ${active ? 'text-primary' : 'text-current'} security-transition`}>
                  {item?.label}
                </span>
                {active && (
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      </nav>
      {/* Safe area for content - prevents overlap */}
      <div className="h-16" />
    </>
  );
};

export default BottomTabNavigation;